// Enhanced analytics tracking utilities

export const GOOGLE_ADS_ID = 'AW-1021920845';
const GOOGLE_ADS_CONTACT_LEAD_CONVERSION_LABEL = 'x8FgCN2T6N4bEM2MpecD';
export const GOOGLE_ADS_PHONE_CONVERSION_LABEL =
  import.meta.env.VITE_GOOGLE_ADS_PHONE_CONVERSION_LABEL || GOOGLE_ADS_CONTACT_LEAD_CONVERSION_LABEL;
export const GOOGLE_ADS_QUOTE_CONVERSION_LABEL =
  import.meta.env.VITE_GOOGLE_ADS_QUOTE_CONVERSION_LABEL || GOOGLE_ADS_CONTACT_LEAD_CONVERSION_LABEL;
export const GOOGLE_ADS_CONTACT_CONVERSION_LABEL =
  import.meta.env.VITE_GOOGLE_ADS_CONTACT_CONVERSION_LABEL || GOOGLE_ADS_CONTACT_LEAD_CONVERSION_LABEL;

export const BUSINESS_PHONE_TEL_URL = 'tel:8005402140';
export const BUSINESS_PHONE_DISPLAY = '(800) 540-2140';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    gtag_report_conversion?: (url?: string) => boolean;
  }
}

function googleAdsSendTo(labelOrSendTo?: string) {
  if (!labelOrSendTo) return undefined;
  return labelOrSendTo.startsWith('AW-')
    ? labelOrSendTo
    : `${GOOGLE_ADS_ID}/${labelOrSendTo}`;
}

// Track page views
export function trackPageView(path: string, title?: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GOOGLE_ADS_ID, {
      page_path: path,
      page_location: window.location.href,
      page_title: title,
    });
  }
}

// Track events
export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
}

// Track conversions
export function trackConversion(conversionLabel?: string, value?: number) {
  const sendTo = googleAdsSendTo(conversionLabel);
  if (!sendTo) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: sendTo,
      value: value ?? 1,
      currency: 'USD',
    });
  }
}

// Track form submissions
export function trackFormSubmission(formName: string, formType: 'contact' | 'quote' | 'newsletter') {
  trackEvent('form_submit', 'engagement', formName);
  trackEvent('form_submit', formType, formName);
  
  if (formType === 'quote') {
    trackConversion(GOOGLE_ADS_QUOTE_CONVERSION_LABEL, 1);
  } else if (formType === 'contact') {
    trackConversion(GOOGLE_ADS_CONTACT_CONVERSION_LABEL, 1);
  }
}

// Track button clicks
export function trackButtonClick(buttonName: string, location: string) {
  trackEvent('click', 'button', `${buttonName} - ${location}`);
}

// Track phone calls
export function trackPhoneCall(phoneNumber = BUSINESS_PHONE_DISPLAY) {
  trackEvent('phone_call', 'engagement', phoneNumber);
  trackConversion(GOOGLE_ADS_PHONE_CONVERSION_LABEL, 1);
}

export function reportPhoneConversion(url = BUSINESS_PHONE_TEL_URL) {
  trackEvent('phone_call_click', 'conversion', BUSINESS_PHONE_DISPLAY);

  if (typeof window === 'undefined') return;

  if (typeof window.gtag_report_conversion === 'function') {
    window.gtag_report_conversion(url);
    return;
  }

  trackConversion(GOOGLE_ADS_PHONE_CONVERSION_LABEL, 1);
  window.location.href = url;
}

// Track quote requests
export function trackQuoteRequest(serviceType: string) {
  trackEvent('quote_request', 'conversion', serviceType);
}

// Track scroll depth
export function trackScrollDepth(depth: number) {
  const milestones = [25, 50, 75, 90, 100];
  const milestone = milestones.find(m => depth >= m && depth < m + 10);
  
  if (milestone) {
    trackEvent('scroll', 'engagement', `${milestone}%`);
  }
}

// Track video plays
export function trackVideoPlay(videoTitle: string) {
  trackEvent('video_play', 'engagement', videoTitle);
}

// Track outbound links
export function trackOutboundLink(url: string, linkText: string) {
  trackEvent('click', 'outbound_link', `${linkText} - ${url}`);
}

// Track search queries
export function trackSearch(query: string, resultsCount: number) {
  trackEvent('search', 'engagement', query, resultsCount);
}
